// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';
import { dispatch } from '../index';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    chats: [],
    user: {},
    users: [],
    hasMoreUsers: true,
    filters: [],
    templates: [],
    gqueue: [],
    queue: [],
    suggestion: {},
    notes: []
};

const slice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        // GET USER
        getUserSuccess(state, action) {
            state.user = action.payload;
        },

        // GET USER CHATS
        getUserChatsSuccess(state, action) {
            state.chats = action.payload;
        },

        getUserQueueSuccess(state, action) {
            state.queue = action.payload;
        },

        getQueueSuccess(state, action) {
            state.gqueue = action.payload;
        },

        // GET USERS
        getUsersSuccess(state, action) {
            state.users = action.payload.users;
            state.hasMoreUsers = action.payload.hasMore;
        },

        // APPEND USERS (for pagination)
        appendUsersSuccess(state, action) {
            state.users = [...state.users, ...action.payload.users];
            state.hasMoreUsers = action.payload.hasMore;
        },

        // RESET USERS (when filters change)
        resetUsers(state) {
            state.users = [];
            state.hasMoreUsers = true;
        },

        // REFRESH USERS (auto refresh without losing pagination)
        refreshUsersSuccess(state, action) {
            state.users = action.payload.users;
            // Keep the existing hasMoreUsers state during refresh
        },

        // Get filters
        getFiltersSuccess(state, action) {
            state.filters = action.payload;
        },

        // Get templates
        getTemplatesSuccess(state, action) {
            state.templates = action.payload;
        },

        getChatSuggestionsSuccess(state, action) {
            state.suggestion = action.payload;
        },

        // GET CHAT NOTES
        getChatNotesSuccess(state, action) {
            state.notes = action.payload;
        },

        // CREATE CHAT NOTE
        createChatNoteSuccess(state, action) {
            state.notes.unshift(action.payload);
            // Trier par date décroissante pour maintenir l'ordre
            state.notes.sort((a, b) => new Date(b.display_date || b.created_at) - new Date(a.display_date || a.created_at));
        },

        // UPDATE CHAT NOTE
        updateChatNoteSuccess(state, action) {
            const index = state.notes.findIndex(note => note.id === action.payload.id);
            if (index !== -1) {
                state.notes[index] = action.payload;
                // Retrier après modification car la date peut avoir changé
                state.notes.sort((a, b) => new Date(b.display_date || b.created_at) - new Date(a.display_date || a.created_at));
            }
        },

        // DELETE CHAT NOTE
        deleteChatNoteSuccess(state, action) {
            state.notes = state.notes.filter(note => note.id !== action.payload);
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function getUser(id) {
    return async () => {
        try {
            const response = await axios.post('/api/chat/users/id', { id });
            dispatch(slice.actions.getUserSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getUserChats(user) {
    return async () => {
        try {
            const response = await axios.post(`/api/chat/${user}`, { user });
            dispatch(slice.actions.getUserChatsSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Translate message
export function translateMessage(messageId) {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await axios.get('/api/chat/translate/' + messageId);
            resolve(response.data.message);
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            reject(error);
        }
    });
}

export function getUserQueue(user) {
    return async () => {
        try {
            const response = await axios.get(`/api/chat/${user}/queue`, { user });
            dispatch(slice.actions.getUserQueueSuccess(response.data.queue));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getGlobalQueue() {
    return async () => {
        try {
            const response = await axios.get('/api/chat/queue', {});
            dispatch(slice.actions.getQueueSuccess(response.data.gqueue));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function retryQueue(id) {
    return new Promise(async (resolve, reject) => {
        try {
            await axios.get(`/api/chat/${id}/queue/retry`, {});
            resolve(true);
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            reject(false);
        }
    });
}

export function approveReservation(conversation) {
    return async () => {
        try {
            await axios.post(`/api/chat/${conversation}/approve`, {});
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function approveHostifyReservation(conversation) {
    return async () => {
        try {
            const approved = await axios.get(`/api/hostify/message/${conversation}/preapprove`, {});
            return approved.data;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function insertChat(chat) {
    return async () => {
        try {
             switch(chat.source) {
                case 'sms':
                    await axios.post(`/api/sms/${chat.to}`, {
                        message: chat.text,
                        person: chat.from
                    });
                    break;
                case 'whatsapp':
                    await axios.post(`/api/whatsapp/${chat.to}`, {
                        message: chat.text,
                        person: chat.from
                    });
                    break;
                default:
                    await axios.post('/api/chat/insert', chat);
                    dispatch(getUserQueue(chat.to));
                    break;
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function sendHostifyMessage(conversation, message) {
    return async () => {
        try {
            await axios.post(`/api/hostify/message/${conversation}/send`, { message });
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getChatSuggestion(conversation, instructions) {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await axios.get(`/api/assistants/suggest/${conversation}?instructions=${instructions}`);
            dispatch(slice.actions.getChatSuggestionsSuccess(response.data.messages));
            return resolve(response.data.messages);
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return reject(error);
        }
    });
}

export function markAsRead(conversation) {
    return async () => {
        try {
            await axios.get(`/api/chat/${conversation}/markasread`, {});
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function changeIaStatus(conversation, status) {
    return async () => {
        try {
            await axios.get(`/api/chat/ia/${conversation}/${status}`, );
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getFilters() {
    return async () => {
        try {
            const response = await axios.get('/api/chat/filters');
            dispatch(slice.actions.getFiltersSuccess(response.data.filters));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getUsers(search, label, unreadMessages, offset = 0, append = false, isRefresh = false) {
    return async () => {
        try {
            // For refresh, we need to load all currently displayed items (up to current offset + 20)
            const limit = isRefresh ? Math.max(offset + 20, 20) : 20;
            const requestOffset = isRefresh ? 0 : offset;
            
            let url = `/api/chat/users?t=${new Date().getTime()}&limit=${limit}&offset=${requestOffset}`
            if(search) url += `&q=${search}`
            if(label) url += `&l=${label}`
            if(unreadMessages) url += `&u=${unreadMessages}`
            const response = await axios.get(url);
            
            if (isRefresh) {
                dispatch(slice.actions.refreshUsersSuccess(response.data));
            } else if (append) {
                dispatch(slice.actions.appendUsersSuccess(response.data));
            } else {
                dispatch(slice.actions.getUsersSuccess(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function resetUsers() {
    return () => {
        dispatch(slice.actions.resetUsers());
    };
}

export function getTemplates(room) {
    return async () => {
        try {
            const response = await axios.get(`/api/templates${room ? '/' + room : ''}`);
            dispatch(slice.actions.getTemplatesSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getTemplateTranslation(id, lang) {
    return new Promise(async (resolve, reject) => {
        try {
            const response = await axios.get(`/api/templates//translate/${id}/${lang}`);
            resolve(response.data.translation);
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            reject(error);
        }
    });
}

// Get chat notes for a conversation thread
export function getChatNotes(threadId) {
    return async () => {
        try {
            const response = await axios.get(`/api/chat/${threadId}/notes`);
            dispatch(slice.actions.getChatNotesSuccess(response.data.notes));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Create a new chat note
export function createChatNote(threadId, noteText) {
    return async () => {
        try {
            const response = await axios.post(`/api/chat/${threadId}/notes`, {
                note: noteText
            });
            dispatch(slice.actions.createChatNoteSuccess(response.data.note));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Update an existing chat note
export function updateChatNote(threadId, noteId, noteText) {
    return async () => {
        try {
            const response = await axios.put(`/api/chat/${threadId}/notes/${noteId}`, {
                note: noteText
            });
            dispatch(slice.actions.updateChatNoteSuccess(response.data.note));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error; // Re-throw pour permettre la gestion d'erreur dans le composant
        }
    };
}

// Delete a chat note
export function deleteChatNote(threadId, noteId) {
    return async () => {
        try {
            await axios.delete(`/api/chat/${threadId}/notes/${noteId}`);
            dispatch(slice.actions.deleteChatNoteSuccess(noteId));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}